import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, Flag, MapPin, Users } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell, AppFooter } from "../components/Shell";
import { BottomNav } from "../components/BottomNav";
import { PairCard } from "../components/PairCard";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useT, useLabels } from "../i18n";
import { raceDateLabel } from "./Races";

export default function RaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { t, fmtDate } = useT();
  const L = useLabels();
  const [race, setRace] = useState(null);
  const [pairs, setPairs] = useState(null);

  useEffect(() => {
    api.get("/races/overview").then((r) => setRace(r.data.find((x) => x.id === id) || false)).catch(() => setRace(false));
    if (user && profile) api.get(`/races/${id}/pairs`).then((r) => setPairs(r.data.pairs)).catch(() => setPairs([]));
  }, [id, user, profile]);

  if (race === null) return <AppShell><TopBar onBack={() => navigate("/courses")} /><p className="p-5 text-sm text-rox-muted">{t("loading")}</p></AppShell>;
  if (race === false) return (
    <AppShell><TopBar onBack={() => navigate("/courses")} />
      <main className="px-5 pt-6 lg:px-8" data-testid="race-not-found"><h1 className="h-display text-4xl">{t("race_not_found")}</h1><Link to="/courses" data-testid="race-not-found-back" className="btn-primary mt-6 lg:max-w-sm">{t("nav_races")} <ArrowRight size={20} /></Link></main>
      <AppFooter withNav={!!user} />{user && <BottomNav />}
    </AppShell>
  );

  return (
    <AppShell>
      <TopBar onBack={() => navigate("/courses")} />
      <main className="px-5 pb-28 pt-2 lg:px-8 lg:pb-16" data-testid="race-detail-page">
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-10">
          <div>
            <p className="eyebrow text-rox-orange">{race.city}</p>
            <h1 className="h-display mt-1 text-4xl lg:text-6xl" data-testid="race-detail-name">{race.name}</h1>
            <p className={`mt-3 font-display text-xl uppercase tracking-wide ${race.date ? "text-rox-orange" : "text-rox-muted"}`} data-testid="race-detail-date">{raceDateLabel(race, fmtDate, t)}</p>
            {!["upcoming", "tbc"].includes(race.status) && <p className="mt-2 inline-block rounded-sm border border-rox-line px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rox-muted">{t(`race_status_${race.status}`)}</p>}
            {race.venue && <p className="mt-4 flex items-start gap-2 text-sm text-rox-off/80"><MapPin size={16} className="mt-0.5 shrink-0 text-rox-orange" /> {race.venue}</p>}
            <div className="mt-5">
              <p className="eyebrow">{t("race_formats")}</p>
              {race.formats?.length ? (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="race-formats">
                  {race.formats.map((f) => <span key={f} className="rounded-[3px] border border-rox-line px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">{L.format(f)}</span>)}
                </div>
              ) : <p className="mt-1 text-sm text-rox-muted">{t("race_formats_tbc")}</p>}
            </div>
            {race.source_url && (
              <a href={race.source_url} target="_blank" rel="noreferrer" data-testid="race-source" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-rox-muted hover:text-rox-orange">
                {t("race_source")} <ExternalLink size={12} />
              </a>
            )}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-2 lg:mt-0 lg:grid-cols-1 lg:self-start">
            <div className="panel p-4"><Users size={18} className="text-rox-orange" /><p className="h-display mt-2 text-5xl" data-testid="race-detail-athletes">{race.athletes}</p><p className="mt-1 text-xs text-rox-off/80">{t("athletes_count", race.athletes).replace(/^\d+\s*/, "")}</p></div>
            <div className="panel p-4"><Flag size={18} className="text-rox-orange" /><p className="h-display mt-2 text-5xl text-rox-orange" data-testid="race-detail-searching">{race.searching}</p><p className="mt-1 text-xs text-rox-off/80">{t("searching_count", race.searching).replace(/^\d+\s*/, "")}</p></div>
          </div>
        </div>

        <h2 className="h-display mt-10 text-3xl">{t("race_compatible")}</h2>
        {!user || !profile ? (
          <Link to={user ? "/onboarding" : "/inscription"} data-testid="race-login-cta" className="btn-primary mt-4 lg:max-w-sm">{t("race_login_cta")} <ArrowRight size={20} /></Link>
        ) : pairs === null ? (
          <p className="mt-3 text-sm text-rox-muted">{t("analyzing")}</p>
        ) : pairs.length === 0 ? (
          <p className="panel mt-4 p-5 text-sm text-rox-off/75" data-testid="race-no-pairs">{t("race_none_compatible")}</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 stagger lg:grid-cols-2 xl:grid-cols-3" data-testid="race-pairs">
            {pairs.map((p) => <PairCard key={p.id} pair={p} />)}
          </div>
        )}
      </main>
      <AppFooter withNav={!!user} />
      {user && <BottomNav />}
    </AppShell>
  );
}
